/**
 * Finding a stored quote back in the rendered gap.
 *
 * A comment anchored to a passage stores that passage and nothing else — see
 * `GapDraftComment.quote`. Which means the anchor has to be RESOLVED at read
 * time, by searching the document on screen for the words the reviewer had
 * selected. That is the whole trade: character offsets would resolve in one
 * lookup and would be wrong the moment a field is edited, translated, or
 * rendered by the other reader view, and a broken offset says nothing to the
 * person reading it. A quote that fails to match still says what was meant.
 *
 * The matching is on RENDERED text, which is not what the DOM holds. Two things
 * have to be reconciled:
 *
 * - Whitespace. JSX indentation puts newlines and runs of spaces inside text
 *   nodes that the browser draws as a single space, and `Selection.toString()`
 *   returns what was drawn. Every whitespace run collapses to one space on both
 *   sides of the comparison.
 * - Block boundaries. `<p>a</p><p>b</p>` concatenates to `ab` in the DOM while a
 *   selection across it reads `a\n\nb`. Entering a block element — or a `<br>` —
 *   contributes a separator, so the flattened text breaks where the page does.
 */

/**
 * How much of a selection is kept as an anchor.
 *
 * Matched by `AddGapDraftCommentDTO`. A quote is a pointer at a passage, and one
 * longer than this is a reviewer selecting a whole section — which the Notes
 * tab's gap-wide composer already covers.
 */
export const QUOTE_MAX_LENGTH = 400;

/** Elements whose boundary the reader sees as a line break. */
const BOUNDARY_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BR',
  'DD',
  'DETAILS',
  'DIV',
  'DL',
  'DT',
  'FIGURE',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'SUMMARY',
  'TABLE',
  'TD',
  'TH',
  'TR',
  'UL',
]);

/** Where one character of the flattened text came from. */
interface CharacterOrigin {
  node: Text;
  offset: number;
}

/** The rendered text of a subtree, with every character traceable back. */
interface FlattenedText {
  text: string;
  /** Index-aligned with `text`. `null` for an injected block separator. */
  origins: (CharacterOrigin | null)[];
}

/**
 * A quote as it is stored and compared: whitespace collapsed, ends trimmed.
 *
 * Applied when the comment is written as well as when it is looked up, so what
 * is in Mongo is already in the comparable form and the two can never disagree
 * about what counts as the same passage.
 */
export function normalizeQuote(quote: string): string {
  return quote.replace(/\s+/g, ' ').trim();
}

/**
 * A selection cut down to an anchor's length, on a word boundary.
 *
 * No ellipsis and no marker: the result is still a literal run of the document,
 * which is what keeps a clipped quote findable. A reviewer who selects six
 * paragraphs gets the first few sentences of them as the anchor.
 */
export function clipQuote(quote: string): string {
  const normalized = normalizeQuote(quote);
  if (normalized.length <= QUOTE_MAX_LENGTH) return normalized;

  const head = normalized.slice(0, QUOTE_MAX_LENGTH);
  const lastSpace = head.lastIndexOf(' ');
  return lastSpace > 0 ? head.slice(0, lastSpace) : head;
}

/**
 * The rendered text under `root`, and where each character lives.
 *
 * Walks elements as well as text so a block boundary can contribute the
 * separator the DOM does not hold. Separators and collapsed whitespace never
 * double up, so the result is the same shape `normalizeQuote` produces.
 */
function flattenRenderedText(root: Node): FlattenedText {
  const characters: string[] = [];
  const origins: (CharacterOrigin | null)[] = [];

  const pushSpace = (origin: CharacterOrigin | null) => {
    // Nothing leads with a space, and two separators in a row are one break.
    if (characters.length === 0) return;
    if (characters[characters.length - 1] === ' ') return;
    characters.push(' ');
    origins.push(origin);
  };

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
  );

  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (BOUNDARY_TAGS.has((node as Element).tagName)) pushSpace(null);
      continue;
    }

    const textNode = node as Text;
    for (let offset = 0; offset < textNode.data.length; offset += 1) {
      const character = textNode.data.charAt(offset);
      if (/\s/.test(character)) {
        pushSpace({ node: textNode, offset });
        continue;
      }
      characters.push(character);
      origins.push({ node: textNode, offset });
    }
  }

  return { text: characters.join(''), origins };
}

/**
 * The first place `quote` appears under `root`, as a live DOM range.
 *
 * First rather than every place: a quote is one reviewer pointing at one
 * passage, and a document that repeats a sentence verbatim is a document where
 * either occurrence answers "what is this comment about".
 *
 * `null` when the passage is not on screen — the field was edited since, or the
 * reader is showing its other view. Callers treat that as "no highlight", never
 * as "no comment".
 */
export function findQuoteRange(root: Node, quote: string): Range | null {
  const needle = normalizeQuote(quote);
  if (needle.length === 0) return null;

  const flattened = flattenRenderedText(root);
  const startIndex = flattened.text.indexOf(needle);
  if (startIndex === -1) return null;

  const start = flattened.origins[startIndex];
  const end = flattened.origins[startIndex + needle.length - 1];
  // A trimmed needle begins and ends on a real character, so both ends trace
  // back to a text node; an injected separator can only fall in the middle.
  if (!start || !end) return null;

  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset + 1);
  return range;
}
